/**
 * Buy Requests page — "طلبات الشراء"
 * -----------------------------------
 * Public marketplace for buyer-posted requests. Users with 4 categories can
 * post a request where instead of a price they publish:
 *   • urgency       (normal | urgent | critical)
 *   • budget_type   (open | fixed | max) + optional amount
 *
 * Merchants / visitors browse requests and respond with their offer.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Phone, Car, Home, RectangleHorizontal,
  Zap, AlertTriangle, Eye, X, MessageCircle, Send, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'vehicle',      icon: Car,                  label_ar: 'المركبات',       label_en: 'Vehicles' },
  { key: 'car_plate',    icon: RectangleHorizontal,  label_ar: 'لوحات سيارات',   label_en: 'Car Plates' },
  { key: 'phone_number', icon: Phone,                label_ar: 'أرقام الهواتف',   label_en: 'Phone Numbers' },
  { key: 'property',     icon: Home,                 label_ar: 'العقارات',       label_en: 'Properties' },
];

const URGENCY_META = {
  normal:   { label_ar: 'عادي',    label_en: 'Normal',   color: 'bg-slate-500/15 text-slate-500',    border: 'border-slate-500/30' },
  urgent:   { label_ar: 'عاجل',    label_en: 'Urgent',   color: 'bg-amber-500/15 text-amber-500',    border: 'border-amber-500/40' },
  critical: { label_ar: 'مستعجل', label_en: 'Critical', color: 'bg-red-500/15 text-red-500',        border: 'border-red-500/40' },
};

const EMIRATES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'رأس الخيمة', 'الفجيرة', 'أم القيوين'];

export default function BuyRequests() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';

  const [category, setCategory] = useState('vehicle');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishOpen, setPublishOpen] = useState(false);

  const fetchItems = async (cat) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/public/buy-requests?category=${cat}&limit=60`);
      setItems(res.data?.requests || []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(category); }, [category]);

  const filtered = useMemo(() => {
    if (urgencyFilter === 'all') return items;
    return items.filter(i => i.urgency === urgencyFilter);
  }, [items, urgencyFilter]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F6F8FB]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className={`relative overflow-hidden pt-16 pb-10 ${isDarkMode ? 'bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#0A0A0A]' : 'bg-gradient-to-br from-[#1E9BD9] via-[#0284C7] to-[#0369A1]'}`}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 end-20 w-96 h-96 bg-[#1E9BD9]/25 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 start-10 w-72 h-72 bg-[#F59E0B]/20 rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-5 border border-white/20">
            <Zap className="h-3.5 w-3.5 text-[#FBBF24]" />
            {isAr ? 'طلبات شراء مباشرة من المشترين' : 'Direct buy requests from buyers'}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-3 text-white tracking-tight leading-[1.1]" data-testid="buy-requests-title">
            {isAr ? 'طلبات الشراء' : 'Buy Requests'}
          </h1>
          <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-6">
            {isAr
              ? 'يرى التجار طلبك فور نشره ويقدمون لك أفضل العروض — بدون سعر محدد مسبقاً.'
              : 'Merchants see your request instantly and come back with their best offers — no pre-set price required.'}
          </p>
          <Button
            onClick={() => setPublishOpen(true)}
            data-testid="buy-requests-publish-btn"
            className="h-12 px-7 bg-white text-[#0F172A] hover:bg-white/90 font-bold rounded-xl shadow-xl shadow-black/20"
          >
            <Plus className="h-5 w-5 me-2" />
            {isAr ? 'انشر طلب شراء' : 'Post a buy request'}
          </Button>
        </div>
      </section>

      {/* Category switch (4-way) */}
      <section className="py-8 -mt-6 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`rounded-2xl p-2 border grid grid-cols-2 md:grid-cols-4 gap-2 ${isDarkMode ? 'bg-[#141414] border-white/10 shadow-2xl shadow-black/40' : 'bg-white border-gray-200/80 shadow-lg'}`}
               data-testid="buy-requests-category-switch">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => { setCategory(c.key); setUrgencyFilter('all'); }}
                  data-testid={`buy-requests-cat-${c.key}`}
                  className={`flex items-center justify-center gap-2 h-14 rounded-xl font-bold transition-all ${
                    active
                      ? 'bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white shadow-lg shadow-[#1E9BD9]/40'
                      : (isDarkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100')
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{isAr ? c.label_ar : c.label_en}</span>
                </button>
              );
            })}
          </div>

          {/* Urgency filter chips */}
          <div className="flex items-center gap-2 mt-5 flex-wrap">
            {['all', 'critical', 'urgent', 'normal'].map(u => {
              const meta = u === 'all' ? null : URGENCY_META[u];
              const active = urgencyFilter === u;
              const label = u === 'all'
                ? (isAr ? 'الكل' : 'All')
                : (isAr ? meta.label_ar : meta.label_en);
              return (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUrgencyFilter(u)}
                  data-testid={`buy-requests-urgency-${u}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    active
                      ? 'bg-[#1E9BD9] text-white border-[#1E9BD9]'
                      : (meta
                          ? `${meta.color} ${meta.border}`
                          : (isDarkMode ? 'border-white/10 text-gray-400' : 'border-gray-200 text-gray-600'))
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Items grid */}
      <section className="py-6 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className={`text-center py-20 rounded-2xl border ${isDarkMode ? 'bg-[#141414] border-white/10 text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
              <p className="text-lg font-medium">{isAr ? 'لا توجد طلبات شراء حالياً' : 'No buy requests yet'}</p>
              <p className="text-sm mt-2">{isAr ? 'كن أول من ينشر طلباً في هذه الفئة.' : 'Be the first to post a request in this category.'}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((r, idx) => (
                <BuyRequestCard key={r.id || idx} request={r} isAr={isAr} isDarkMode={isDarkMode} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Publish modal */}
      {publishOpen && (
        <PublishModal
          category={category}
          isAr={isAr}
          isDarkMode={isDarkMode}
          onClose={() => setPublishOpen(false)}
          onPublished={() => { setPublishOpen(false); fetchItems(category); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Request card
// ─────────────────────────────────────────────────────────
function BuyRequestCard({ request, isAr, isDarkMode }) {
  const uMeta = URGENCY_META[request.urgency] || URGENCY_META.normal;
  const budgetText = () => {
    if (request.budget_type === 'open') return isAr ? 'السعر مفتوح للعروض' : 'Open to offers';
    if (request.budget_type === 'fixed') return `${request.budget_amount} ${request.currency || 'AED'}`;
    return `${isAr ? 'حتى' : 'Up to'} ${request.budget_amount} ${request.currency || 'AED'}`;
  };
  return (
    <div
      className={`group relative rounded-2xl p-5 flex flex-col gap-3 transition-all duration-300 hover:-translate-y-1 ${
        isDarkMode
          ? 'bg-[#141414] border border-white/5 hover:border-[#1E9BD9]/30 shadow-xl shadow-black/40'
          : 'bg-white border border-gray-200/80 shadow-sm hover:shadow-lg hover:border-[#1E9BD9]/30'
      }`}
      data-testid={`buy-request-card-${request.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${uMeta.color} ${uMeta.border}`}>
          {request.urgency === 'critical' && <AlertTriangle className="h-3 w-3" />}
          {isAr ? uMeta.label_ar : uMeta.label_en}
        </span>
        <span className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <Eye className="h-3.5 w-3.5" /> {request.views || 0}
        </span>
      </div>
      <h3 className={`text-base font-bold line-clamp-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
        {request.title}
      </h3>
      <p className={`text-sm line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{request.description}</p>
      <div className={`flex items-center justify-between pt-3 mt-auto border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
        <span className={`text-sm font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
          {budgetText()}
        </span>
        <a
          href={`https://wa.me/${(request.contact_phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(isAr ? 'بخصوص طلب الشراء: ' + request.title : 'Regarding buy request: ' + request.title)}`}
          target="_blank" rel="noreferrer"
          data-testid={`buy-request-contact-${request.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-[#1E9BD9]/10 text-[#1E9BD9] hover:bg-[#1E9BD9] hover:text-white transition-colors"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {isAr ? 'تواصل' : 'Contact'}
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Publish modal
// ─────────────────────────────────────────────────────────
function PublishModal({ category: initialCategory, isAr, isDarkMode, onClose, onPublished }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: initialCategory,
    title: '',
    description: '',
    urgency: 'normal',
    budget_type: 'open',
    budget_amount: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
  });

  const token = localStorage.getItem('token');

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      return;
    }
    if (!form.title.trim() || !form.description.trim() || !form.contact_name.trim() || !form.contact_phone.trim()) {
      toast.error(isAr ? 'يرجى تعبئة الحقول المطلوبة' : 'Please fill the required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget_amount: form.budget_type === 'open' ? null : Number(form.budget_amount || 0),
      };
      await axios.post(`${BACKEND_URL}/api/customer/buy-requests`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isAr ? 'تم نشر طلب الشراء ✓' : 'Buy request published ✓');
      onPublished?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'تعذّر نشر الطلب' : 'Failed to publish'));
    } finally {
      setSubmitting(false);
    }
  };

  const bg = isDarkMode ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-gray-200 text-[#0f172a]';
  const labelCls = isDarkMode ? 'text-gray-300' : 'text-gray-700';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className={`w-full max-w-xl rounded-2xl border max-h-[90vh] overflow-y-auto ${bg}`}
        onClick={e => e.stopPropagation()}
        data-testid="buy-requests-publish-modal"
      >
        <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
            {isAr ? 'نشر طلب شراء' : 'Post a buy request'}
          </h3>
          <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Category */}
          <div>
            <Label className={labelCls}>{isAr ? 'الفئة' : 'Category'}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const active = form.category === c.key;
                return (
                  <button
                    type="button"
                    key={c.key}
                    onClick={() => setForm(f => ({ ...f, category: c.key }))}
                    data-testid={`publish-cat-${c.key}`}
                    className={`flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold border transition-all ${
                      active
                        ? 'bg-[#1E9BD9]/10 border-[#1E9BD9] text-[#1E9BD9]'
                        : (isDarkMode ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600')
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {isAr ? c.label_ar : c.label_en}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className={labelCls}>{isAr ? 'عنوان الطلب' : 'Title'} *</Label>
            <Input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder={isAr ? 'مثال: أبحث عن تويوتا كامري 2022 نظيفة' : 'e.g. Looking for a clean Camry 2022'}
              className={`mt-2 ${inputCls}`}
              data-testid="publish-title-input"
            />
          </div>

          <div>
            <Label className={labelCls}>{isAr ? 'التفاصيل' : 'Description'} *</Label>
            <Textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={4}
              placeholder={isAr ? 'المواصفات المطلوبة، حالة السلعة، أي ملاحظات…' : 'Specs, condition, any notes…'}
              className={`mt-2 ${inputCls}`}
              data-testid="publish-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>{isAr ? 'الاستعجال' : 'Urgency'}</Label>
              <select
                value={form.urgency}
                onChange={e => setForm({ ...form, urgency: e.target.value })}
                className={`w-full mt-2 h-10 px-3 rounded-md border text-sm ${inputCls}`}
                data-testid="publish-urgency-select"
              >
                <option value="normal">{isAr ? 'عادي' : 'Normal'}</option>
                <option value="urgent">{isAr ? 'عاجل' : 'Urgent'}</option>
                <option value="critical">{isAr ? 'مستعجل' : 'Critical'}</option>
              </select>
            </div>
            <div>
              <Label className={labelCls}>{isAr ? 'الميزانية' : 'Budget'}</Label>
              <select
                value={form.budget_type}
                onChange={e => setForm({ ...form, budget_type: e.target.value })}
                className={`w-full mt-2 h-10 px-3 rounded-md border text-sm ${inputCls}`}
                data-testid="publish-budget-type-select"
              >
                <option value="open">{isAr ? 'مفتوحة' : 'Open'}</option>
                <option value="fixed">{isAr ? 'سعر محدد' : 'Fixed price'}</option>
                <option value="max">{isAr ? 'حد أقصى' : 'Max budget'}</option>
              </select>
            </div>
          </div>

          {form.budget_type !== 'open' && (
            <div>
              <Label className={labelCls}>{isAr ? 'المبلغ بالدرهم' : 'Amount in AED'}</Label>
              <Input
                type="number"
                value={form.budget_amount}
                onChange={e => setForm({ ...form, budget_amount: e.target.value })}
                className={`mt-2 ${inputCls}`}
                data-testid="publish-budget-amount-input"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>{isAr ? 'اسمك' : 'Your name'} *</Label>
              <Input
                value={form.contact_name}
                onChange={e => setForm({ ...form, contact_name: e.target.value })}
                className={`mt-2 ${inputCls}`}
                data-testid="publish-name-input"
              />
            </div>
            <div>
              <Label className={labelCls}>{isAr ? 'الهاتف' : 'Phone'} *</Label>
              <Input
                value={form.contact_phone}
                onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                placeholder="+971 50 123 4567"
                className={`mt-2 ${inputCls}`}
                data-testid="publish-phone-input"
              />
            </div>
          </div>

          <div className={`flex items-center justify-between pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="publish-cancel-btn">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="publish-submit-btn"
              className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white px-6"
            >
              {submitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Send className="h-4 w-4 me-2" />}
              {isAr ? 'نشر الطلب' : 'Publish'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
